export type MembershipPaymentResultPromotionNotice = {
  enabled: boolean;
  title: string;
  description: string;
};

export type MembershipPaymentResult = {
  productName: string;
  subscriptionNo?: number;
  paymentAmount?: number;
  paymentAmountLabel: string;
  paymentMethodLabel: string;
  periodLabel: string;
  nextPaymentDateLabel: string;
  status?: 'ACTIVE' | 'PENDING';
  statusLabel: string;
  promotionNotice: MembershipPaymentResultPromotionNotice;
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null;
};

const readField = (
  record: Record<string, unknown>,
  camelCaseKey: string,
  pascalCaseKey: string,
) => {
  return record[camelCaseKey] ?? record[pascalCaseKey];
};

const readString = (value: unknown, fallback = '') => {
  return typeof value === 'string' && value.length > 0 ? value : fallback;
};

const readLabel = (value: unknown, fallback = '') => {
  return typeof value === 'string' ? value : fallback;
};

const readNumber = (value: unknown) => {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
};

const readBoolean = (value: unknown, fallback = false) => {
  return typeof value === 'boolean' ? value : fallback;
};

const getCplatResponseBody = (response: unknown) => {
  if (!isRecord(response)) return undefined;

  const axiosBody = response.data;
  if (isRecord(axiosBody) && typeof axiosBody.code === 'string') return axiosBody;
  if (typeof response.code === 'string') return response;

  return undefined;
};

const getResponseData = (body: Record<string, unknown>) => {
  const data = body.data;
  const firstData = Array.isArray(data) ? data[0] : data;

  return isRecord(firstData) ? firstData : undefined;
};

export const getPaymentResultIdFromResponse = (response: unknown) => {
  const body = getCplatResponseBody(response);
  if (!body) return undefined;

  const bodyPaymentResultId = readString(readField(body, 'paymentResultId', 'PaymentResultId'), '');
  if (bodyPaymentResultId) return bodyPaymentResultId;

  const data = getResponseData(body);
  if (!data) return undefined;

  return (
    readString(readField(data, 'paymentResultId', 'PaymentResultId'), '') ||
    readString(readField(data, 'resultId', 'ResultId'), '') ||
    undefined
  );
};

export const createPaymentResultPath = (path: string, paymentResultId?: string) => {
  if (!paymentResultId) return path;

  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}paymentResultId=${encodeURIComponent(paymentResultId)}`;
};

export const createMembershipPaymentResultPath = ({
  path,
  result,
  paymentChange = false,
  paymentResultId,
}: {
  path: string;
  result: 'success' | 'fail';
  paymentChange?: boolean;
  paymentResultId?: string;
}) => {
  const params = new URLSearchParams({ result });
  if (paymentChange) params.set('paymentChange', 'true');
  if (paymentResultId) params.set('paymentResultId', paymentResultId);

  return `${path}?${params.toString()}`;
};

export const resolveMembershipPaymentResult = (
  response: unknown,
): MembershipPaymentResult | undefined => {
  const body = getCplatResponseBody(response);
  if (!body || body.code !== '200') return undefined;

  const data = getResponseData(body);
  if (!data) return undefined;

  const promotionNoticeData = readField(data, 'promotionNotice', 'PromotionNotice');
  const promotionNotice = isRecord(promotionNoticeData) ? promotionNoticeData : {};

  const paymentAmount = readNumber(readField(data, 'paymentAmount', 'PaymentAmount'));
  const paymentAmountLabel = readString(
    readField(data, 'paymentAmountLabel', 'PaymentAmountLabel'),
    paymentAmount !== undefined ? `${paymentAmount.toLocaleString('ko-KR')}원` : '',
  );

  return {
    productName: readString(readField(data, 'productName', 'ProductName'), '셀러박스 멤버십'),
    subscriptionNo: readNumber(readField(data, 'subscriptionNo', 'SubscriptionNo')),
    paymentAmount,
    paymentAmountLabel,
    paymentMethodLabel: readString(
      readField(data, 'paymentMethodLabel', 'PaymentMethodLabel'),
      '등록한 결제 수단',
    ),
    periodLabel: readLabel(readField(data, 'periodLabel', 'PeriodLabel'), '앱에서 확인 가능'),
    nextPaymentDateLabel: readLabel(
      readField(data, 'nextPaymentDateLabel', 'NextPaymentDateLabel'),
      '앱에서 확인 가능',
    ),
    status: readMembershipPaymentResultStatus(readField(data, 'status', 'Status')),
    statusLabel: readString(readField(data, 'statusLabel', 'StatusLabel'), '이용중'),
    promotionNotice: {
      enabled: readBoolean(readField(promotionNotice, 'enabled', 'Enabled'), false),
      title: readString(readField(promotionNotice, 'title', 'Title'), ''),
      description: readString(readField(promotionNotice, 'description', 'Description'), ''),
    },
  };
};

const readMembershipPaymentResultStatus = (value: unknown) => {
  if (value === 'ACTIVE' || value === 'PENDING') return value;

  return undefined;
};
